/**
 * 캐릭터 카드 임포터 — 결정 #6(풀 호환 임포트).
 *
 * 같은 봇이라도 export 출처에 따라 스키마가 다르다(`docs/risu-migration.md` §1.2).
 * 따라서 3단계 폴백으로 파싱한다:
 *   1) CCv3/V2 표준 스키마 (`spec: chara_card_v3|v2`, `data: {...}`)
 *   2) extensions.risuai 등 확장 필드
 *   3) V1 평면 스키마 (최상위에 name/description...)
 *
 * 플랫폼 중립(결정 #10): 도메인 엔티티만 만든다. 바이트(아바타/에셋)는 다루지
 * 않고, 호출부(web)가 PNG 자체를 아바타로 임포트한다(결정 #18). 손실 방지를 위해
 * 매핑하지 않은 원본 필드는 `extensions`에 보존한다(라운드트립, 결정 #6).
 */
import { createCharacter, createLorebook, createLorebookEntry } from "../factory/entities.js";
import type {
  Character,
  Lorebook,
  LorebookEntry,
  LorebookPosition,
} from "../domain/index.js";
import {
  base64ToUtf8,
  extractPngEmbeddedAssets,
  extractPngTextChunks,
  extractPngTextChunksDecoded,
  isPng,
  type PngEmbeddedAsset,
  type PngTextChunk,
} from "./png.js";
import { isZip, readZipEntries, readZipEntryData, readZipTextFile } from "./zip.js";

/**
 * CHARX에서 추출한 임베드 에셋(결정 #6/#18). 호출부(web)가 콘텐츠 주소화 저장 후
 * 캐릭터에 연결한다. `role`은 CCv3 assets의 `type`("icon"=아바타 등)을 따른다.
 */
export interface ImportedAsset {
  /** CCv3 asset name. */
  name: string;
  /** CCv3 asset type: "icon" | "background" | "emotion" | "user_icon" | ... */
  role: string;
  /** 추론된 MIME 타입. */
  mime: string;
  /** 원본 바이트. */
  bytes: Uint8Array;
}

/** 임포트 결과: 캐릭터 + 선택적 임베드 로어북(연결은 호출부가). */
export interface ImportedCard {
  character: Character;
  lorebook?: Lorebook;
  /** PNG 카드면 true — 호출부가 이 PNG 바이트를 아바타로 임포트하면 된다. */
  fromPng: boolean;
  /** CHARX 등에서 추출한 임베드 에셋(아바타 등). 호출부가 저장·연결한다. */
  assets?: ImportedAsset[];
  /** 매핑하지 못해 무시한/보존한 필드 키(호환성 리포트용, 결정 #6 §6단계). */
  unmappedKeys: string[];
}

type AnyObj = Record<string, unknown>;

function str(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}
function strArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

/**
 * 파일 바이트에서 카드를 임포트한다(동기). PNG(비압축 tEXt ccv3/chara) 또는
 * JSON을 지원한다. zTXt(압축) 청크는 동기 경로에서 풀 수 없으므로, 압축 카드까지
 * 지원하려면 {@link importCardFromBytesAsync}를 쓴다.
 * CHARX(zip)는 후속 작업.
 */
export function importCardFromBytes(bytes: Uint8Array): ImportedCard {
  if (isPng(bytes)) {
    return importFromPngChunks(extractPngTextChunks(bytes));
  }
  // JSON으로 시도.
  const text = new TextDecoder("utf-8").decode(bytes);
  return { ...importFromJsonText(text), fromPng: false };
}

/**
 * 파일 바이트에서 카드를 임포트한다(비동기). 동기 경로와 동일하되 PNG zTXt(압축)
 * 청크와 CHARX(zip 컨테이너의 card.json)까지 처리한다(결정 #6). 브라우저/Node20에서
 * 권장 경로다.
 */
export async function importCardFromBytesAsync(bytes: Uint8Array): Promise<ImportedCard> {
  if (isPng(bytes)) {
    return importFromPngChunks(await extractPngTextChunksDecoded(bytes));
  }
  if (isZip(bytes)) {
    // CHARX: 루트의 card.json을 읽어 매핑하고, embeded:// 에셋을 추출한다(결정 #6).
    const json = await readZipTextFile(bytes, "card.json");
    if (!json) {
      throw new Error("CHARX(zip)에서 card.json을 찾을 수 없습니다.");
    }
    const base = importFromJsonText(json);
    const assets = await resolveCharxAssets(bytes, json);
    return { ...base, fromPng: false, ...(assets.length ? { assets } : {}) };
  }
  const text = new TextDecoder("utf-8").decode(bytes);
  return { ...importFromJsonText(text), fromPng: false };
}

/**
 * CHARX의 CCv3 `data.assets`를 읽어 `embeded://` URI가 가리키는 ZIP 항목을
 * 바이트로 추출한다(결정 #6/#18). 호출부(web)가 콘텐츠 주소화 저장·연결한다.
 */
async function resolveCharxAssets(zipBytes: Uint8Array, cardJson: string): Promise<ImportedAsset[]> {
  let parsed: AnyObj;
  try {
    parsed = JSON.parse(cardJson) as AnyObj;
  } catch {
    return [];
  }
  const data = (parsed["data"] && typeof parsed["data"] === "object"
    ? parsed["data"]
    : parsed) as AnyObj;
  const rawAssets = Array.isArray(data["assets"]) ? (data["assets"] as AnyObj[]) : [];
  if (rawAssets.length === 0) return [];

  const entries = readZipEntries(zipBytes);
  const out: ImportedAsset[] = [];

  for (const a of rawAssets) {
    const uri = str(a["uri"]);
    if (!uri || !uri.startsWith("embeded://")) continue; // 외부 URL/data URI는 스킵
    const path = uri.slice("embeded://".length);
    const entry = entries.find((e) => e.name === path || e.name.endsWith(`/${path}`));
    if (!entry) continue;
    const bytes = await readZipEntryData(zipBytes, entry);
    if (!bytes) continue;
    out.push({
      name: str(a["name"]) ?? path,
      role: str(a["type"]) ?? "other",
      mime: mimeFromExt(str(a["ext"]) ?? path),
      bytes,
    });
  }
  return out;
}

/** 확장자/경로에서 이미지 MIME을 추론(에셋 저장 메타용). */
function mimeFromExt(s: string): string {
  const ext = s.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    case "avif":
      return "image/avif";
    case "svg":
      return "image/svg+xml";
    default:
      return "application/octet-stream";
  }
}

/** 추출된 PNG text 청크에서 카드 JSON을 뽑아 파싱한다(ccv3 우선, chara 폴백). */
function importFromPngChunks(chunks: PngTextChunk[]): ImportedCard {
  // ccv3 우선, 없으면 chara(V2/V1).
  const byKeyword = (kw: string) =>
    chunks.find((c) => c.keyword.toLowerCase() === kw && typeof c.text === "string");
  const ccv3 = byKeyword("ccv3");
  const chara = byKeyword("chara");
  const source = ccv3 ?? chara;
  if (!source?.text) {
    throw new Error("PNG에서 캐릭터 카드 데이터를 찾을 수 없습니다 (ccv3/chara 청크 없음).");
  }
  let json: string;
  try {
    json = base64ToUtf8(source.text);
  } catch {
    // 일부 카드는 base64가 아니라 평문 JSON을 넣기도 함.
    json = source.text;
  }
  const base = importFromJsonText(json);
  // 추가 에셋 청크(chara-ext-asset_:{path})를 카드의 asset 선언과 매칭(결정 #6).
  const embedded = extractPngEmbeddedAssets(chunks);
  const assets = embedded.length ? resolvePngAssets(embedded, json) : [];
  return { ...base, fromPng: true, ...(assets.length ? { assets } : {}) };
}

/**
 * PNG 임베드 에셋(`chara-ext-asset_:{path}` 청크에서 디코드된 바이트)을 카드 JSON의
 * asset 선언과 매칭한다(결정 #6/#18). 두 출처를 모두 본다:
 *  - CCv3 표준: `data.assets[] = { name, type, uri:"embeded://{path}", ext }`.
 *  - RisuAI 내부: `emotionImages: [emotion, uri][]`, `additionalAssets: [name, uri, ext][]`.
 * 둘 다 없으면(선언 없이 청크만 있는 경우) path에서 역할/이름을 추론해 보존한다.
 */
function resolvePngAssets(embedded: PngEmbeddedAsset[], cardJson: string): ImportedAsset[] {
  const byPath = new Map<string, Uint8Array>();
  for (const a of embedded) byPath.set(a.path, a.bytes);

  let parsed: AnyObj | undefined;
  try {
    parsed = JSON.parse(cardJson) as AnyObj;
  } catch {
    parsed = undefined;
  }
  const data = (parsed && parsed["data"] && typeof parsed["data"] === "object"
    ? parsed["data"]
    : parsed) as AnyObj | undefined;

  const out: ImportedAsset[] = [];
  const claimed = new Set<string>();

  /** uri(embeded://path 또는 __asset:path 또는 path)에서 청크 path를 뽑아 매칭. */
  const matchUri = (uri: string | undefined): string | undefined => {
    if (!uri) return undefined;
    let path = uri;
    if (path.startsWith("embeded://")) path = path.slice("embeded://".length);
    else if (path.startsWith("__asset:")) path = path.slice("__asset:".length);
    else if (/^https?:\/\//.test(path)) return undefined; // 외부 URL은 스킵
    if (byPath.has(path)) return path;
    // 일부 카드는 인덱스만 저장(`0`,`1`...) — 그대로 키로 시도.
    return byPath.has(uri) ? uri : undefined;
  };

  const add = (path: string, name: string, role: string, ext?: string) => {
    if (claimed.has(path)) return;
    const bytes = byPath.get(path);
    if (!bytes) return;
    claimed.add(path);
    out.push({ name, role, mime: mimeFromExt(ext ?? path), bytes });
  };

  if (data) {
    // CCv3 data.assets
    const rawAssets = Array.isArray(data["assets"]) ? (data["assets"] as AnyObj[]) : [];
    for (const a of rawAssets) {
      const path = matchUri(str(a["uri"]));
      if (!path) continue;
      add(path, str(a["name"]) ?? path, str(a["type"]) ?? "other", str(a["ext"]));
    }
    // RisuAI emotionImages: [emotion, uri][]
    const emotions = Array.isArray(data["emotionImages"]) ? (data["emotionImages"] as unknown[]) : [];
    for (const pair of emotions) {
      if (!Array.isArray(pair) || pair.length < 2) continue;
      const emotion = str(pair[0]) ?? "emotion";
      const path = matchUri(str(pair[1]));
      if (!path) continue;
      add(path, emotion, "emotion");
    }
    // RisuAI additionalAssets: [name, uri, ext][]
    const additional = Array.isArray(data["additionalAssets"])
      ? (data["additionalAssets"] as unknown[])
      : [];
    for (const triple of additional) {
      if (!Array.isArray(triple) || triple.length < 2) continue;
      const name = str(triple[0]) ?? "asset";
      const path = matchUri(str(triple[1]));
      if (!path) continue;
      add(path, name, "other", str(triple[2]));
    }
  }

  // 선언과 매칭 안 된 청크도 손실 없이 보존(결정 #6). path의 파일명을 이름으로.
  for (const { path } of embedded) {
    if (claimed.has(path)) continue;
    const name = path.split("/").pop() ?? path;
    add(path, name, "other");
  }

  return out;
}

/** JSON 텍스트(또는 객체)에서 카드를 파싱한다. */
function importFromJsonText(text: string): Omit<ImportedCard, "fromPng"> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("카드 JSON을 해석할 수 없습니다.");
  }
  return mapCard(parsed as AnyObj);
}

/** 파싱된 객체를 도메인 엔티티로 매핑(3단계 폴백). */
function mapCard(root: AnyObj): Omit<ImportedCard, "fromPng"> {
  // 1) CCv3/V2: { spec, data }. 2) V1: 평면.
  const spec = str(root["spec"]);
  const data: AnyObj = spec && root["data"] && typeof root["data"] === "object"
    ? (root["data"] as AnyObj)
    : root;

  const unmapped: string[] = [];
  const known = new Set([
    "name",
    "description",
    "personality",
    "scenario",
    "first_mes",
    "mes_example",
    "system_prompt",
    "post_history_instructions",
    "alternate_greetings",
    "tags",
    "creator",
    "creator_notes",
    "character_version",
    "character_book",
    "extensions",
    "nickname",
    "source",
    "group_only_greetings",
    "avatar",
  ]);
  for (const k of Object.keys(data)) {
    if (!known.has(k)) unmapped.push(k);
  }

  // 임베드 로어북(character_book) 매핑.
  let lorebook: Lorebook | undefined;
  const book = data["character_book"];
  if (book && typeof book === "object") {
    lorebook = mapLorebook(book as AnyObj);
  }

  const extensions: AnyObj = {};
  if (data["extensions"] && typeof data["extensions"] === "object") {
    extensions["ccv3"] = data["extensions"];
  }
  // 매핑 안 된 필드 보존(라운드트립, 결정 #6).
  for (const k of unmapped) extensions[k] = data[k];

  const name = str(data["name"]) ?? "이름 없는 캐릭터";
  const character = createCharacter({
    name,
    description: str(data["description"]) ?? "",
    firstMessage: str(data["first_mes"]) ?? "",
    alternateGreetings: strArr(data["alternate_greetings"]),
    tags: strArr(data["tags"]),
    ...(str(data["personality"]) ? { personality: str(data["personality"]) } : {}),
    ...(str(data["scenario"]) ? { scenario: str(data["scenario"]) } : {}),
    ...(str(data["mes_example"]) ? { messageExample: str(data["mes_example"]) } : {}),
    ...(str(data["system_prompt"]) ? { systemPrompt: str(data["system_prompt"]) } : {}),
    ...(str(data["post_history_instructions"])
      ? { postHistoryInstructions: str(data["post_history_instructions"]) }
      : {}),
    ...(str(data["creator_notes"]) ? { creatorNotes: str(data["creator_notes"]) } : {}),
    ...(lorebook ? { lorebookId: lorebook.id } : {}),
    ...(Object.keys(extensions).length ? { extensions } : {}),
    share: {
      ...(str(data["creator"]) ? { author: str(data["creator"]) } : {}),
      ...(str(data["character_version"])
        ? { creatorVersion: str(data["character_version"]) }
        : {}),
      ...(str(data["source"]) ? { sourceUrl: str(data["source"]) } : {}),
    },
  });

  return { character, ...(lorebook ? { lorebook } : {}), unmappedKeys: unmapped };
}

/**
 * CCv3 character_book / ST world info → 우리 Lorebook 매핑. entries는 CCv3의
 * 배열형과 ST의 객체형({"0": {...}}) 둘 다 지원한다(결정 #6).
 */
export function mapLorebook(book: AnyObj): Lorebook {
  const rawEntriesValue = book["entries"];
  const rawEntries: AnyObj[] = Array.isArray(rawEntriesValue)
    ? (rawEntriesValue as AnyObj[])
    : rawEntriesValue && typeof rawEntriesValue === "object"
      ? (Object.values(rawEntriesValue as Record<string, AnyObj>) as AnyObj[])
      : [];
  const entries: LorebookEntry[] = rawEntries.map((e) => {
    const position = mapPosition(e["position"], e["extensions"]);
    return createLorebookEntry({
      content: str(e["content"]) ?? "",
      keys: strArr(e["keys"]),
      secondaryKeys: strArr(e["secondary_keys"]),
      constant: e["constant"] === true,
      disabled: e["enabled"] === false,
      caseSensitive: e["case_sensitive"] === true,
      insertionOrder: typeof e["insertion_order"] === "number" ? (e["insertion_order"] as number) : 0,
      position,
      ...(str(e["comment"]) || str(e["name"])
        ? { comment: str(e["comment"]) ?? str(e["name"]) }
        : {}),
      ...(typeof e["priority"] === "number" ? { probability: e["priority"] as number } : {}),
    });
  });

  return createLorebook({
    name: str(book["name"]) ?? "가져온 로어북",
    ...(str(book["description"]) ? { description: str(book["description"]) } : {}),
    entries,
    scanDepth: typeof book["scan_depth"] === "number" ? (book["scan_depth"] as number) : 4,
    recursiveScanning: book["recursive_scanning"] === true,
    ...(typeof book["token_budget"] === "number" ? { tokenBudget: book["token_budget"] as number } : {}),
  });
}

/** ST/CCv3 position(숫자/문자) → 우리 LorebookPosition. */
function mapPosition(pos: unknown, _ext: unknown): LorebookPosition {
  // CCv3: position은 "before_char" | "after_char" 등 문자열이거나 0/1 숫자.
  if (typeof pos === "string") {
    const p = pos.replace(/_/g, "-");
    if (p === "before-char" || p === "after-char" || p === "before-an" || p === "after-an" || p === "at-depth") {
      return p;
    }
  }
  if (pos === 0) return "before-char";
  if (pos === 1) return "after-char";
  return "before-char";
}
